import React, { Component } from 'react';
import { NavLink } from 'react-router-dom';
import Assessment from '../common/icons/Assessment';
import { SuggestionsButton } from './styled/SuggestionsButton';
import { LogoIcon, Wrapper, MenuWrapper, DurationWrapper, Dot, StatusIndicator, RecordingWrapper } from './styled/HeaderStyled';
import Menu from './Menu/HeaderMenu';
import Rank from './Rank/HeaderRank';
import { colors, routes } from '../../utils/utils';
import { formatTime } from '../../utils/date';
import Placeholder from '../common/Placeholder';
import { getDisplayRecordingState } from '../../store/uiConfig';
import type { TrainerSpottingScore, UserScore, UserScoreError } from '../../store/types';

interface HeaderProps {
    elapsedSeconds?: number | null;
    location: { pathname: string };
    localization: { getText: (key: string) => string };
    hasError: boolean;
    showBoxShadow: boolean;
    userScore?: UserScore | null;
    userScoreError?: UserScoreError | null;
    whitelistPhrasesSpottingScore?: TrainerSpottingScore | null;
    isTrainerActive: boolean;
    isUpdatingTrainerStatus?: boolean;
    displaySnoozingControls: boolean;
    displayOnlineButton: boolean;
    resumeTrainer: () => void;
    snoozeTrainer: (value: number) => void;
    setInfinitySnooze: () => void;
    showDashboardLink: boolean;
    isVodafone?: boolean;
    optInOn: boolean;
    isOptOut: boolean;
    manualOptIn: () => void;
    manualOptOut: () => void;
    doLogout?: () => void;
    isCallStarted: boolean;
    displayCallOptOutControls?: boolean;
    recordCall?: boolean;
    authWithPasswordRequired?: boolean;
    showMenu?: boolean;
    authToken?: string;
    showSuggestionsButton?: boolean;
    closeCallSuggestions?: () => void;
    toggleCallSuggestions: () => void;
    isCallSuggestionsNew: boolean;
    enableTrainer: boolean;
    enableCallSimulator: boolean;
    enableSales: boolean;
}

class Header extends Component<HeaderProps> {
    static defaultProps: Partial<HeaderProps> = {
        elapsedSeconds: null,
        userScore: null,
        userScoreError: null,
        whitelistPhrasesSpottingScore: null,
        isUpdatingTrainerStatus: false,
        isVodafone: false,
        displayCallOptOutControls: false,
        recordCall: true,
        authWithPasswordRequired: false,
        showMenu: true,
        doLogout: undefined,
        authToken: undefined,
        showSuggestionsButton: false
    };

    state = {};

    _renderDuration() {
        const { elapsedSeconds, isCallStarted } = this.props;
        const duration = formatTime(elapsedSeconds || 0);
        if (!duration || !isCallStarted) return;

        return (
            <DurationWrapper $disabled={false}>
                <span>{duration}</span>
            </DurationWrapper>
        );
    }

    _renderRecording() {
        const { isCallStarted, recordCall } = this.props;
        if (!isCallStarted) return null;
        if (!recordCall) return null;
        if (!getDisplayRecordingState()) return null;
        return (
            <div
                style={{
                    width: 12,
                    height: 12,
                    background: 'red',
                    borderRadius: 12
                }}
            />
        );
    }

    _renderLogoNavigation() {
        return (
            <NavLink to={routes.TRAINER_PAGE}>
                <LogoIcon />
            </NavLink>
        );
    }

    _renderSpinner() {
        return (
            <div>
                <Placeholder width="22px" />
            </div>
        );
    }

    _renderUserScores() {
        const { userScore, whitelistPhrasesSpottingScore, localization } = this.props;
        if (!userScore) {
            return null;
        }

        const rankData = userScore;
        const spottingScore = whitelistPhrasesSpottingScore || undefined;

        return (
            <Rank
                score={rankData.score || 0}
                rank={rankData.rank || 0}
                percentage={rankData.percentage || 0}
                whitelistPhrasesSpottingScore={spottingScore}
                localization={localization}
            />
        );
    }

    _renderLogo = () => {
        const {
            location: { pathname },
            userScore,
            userScoreError,
            isTrainerActive
        } = this.props;

        if (pathname !== routes.TRAINER_PAGE) return this._renderLogoNavigation();

        if (!userScoreError && userScore == null && isTrainerActive) {
            return this._renderSpinner();
        }

        if (userScore && this.props.enableTrainer) return this._renderUserScores();

        return <LogoIcon />;
    };

    _renderOnlineButton = () => {
        if (!this.props.displayOnlineButton) {
            return null;
        }

        return (
            <div>
                <StatusIndicator onClick={() => this.props.setInfinitySnooze()}>
                    <span>Online</span>
                    <Dot>
                        <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M8.30536 0L10 1.49647L5.4996 5.47059C5.10791 5.81647 4.59632 6 4.04476 6C3.49321 6 2.97362 5.80941 2.58993 5.46353L0 3.18353L1.69464 1.68706L4.04476 3.76235L8.30536 0Z"
                                fill="white"
                            />
                        </svg>
                    </Dot>
                </StatusIndicator>
            </div>
        );
    };

    render() {
        const {
            hasError,
            localization,
            showBoxShadow,
            displaySnoozingControls,
            isTrainerActive,
            resumeTrainer,
            snoozeTrainer,
            showDashboardLink,
            setInfinitySnooze,
            showMenu,
            authToken,
            doLogout,
            authWithPasswordRequired,
            showSuggestionsButton,
            isCallSuggestionsNew,
            enableCallSimulator
        } = this.props;

        return (
            <Wrapper $showBoxShadow={showBoxShadow}>
                <div>{this._renderLogo()}</div>
                <RecordingWrapper>
                    {this._renderDuration()}
                    {this._renderRecording()}
                </RecordingWrapper>
                {this._renderOnlineButton()}
                <div>
                    {showSuggestionsButton && (
                        <SuggestionsButton onClick={this.props.toggleCallSuggestions}>
                            <Assessment size={22} color={colors.black.nero} />
                            {isCallSuggestionsNew && <div className="red-dot" />}
                        </SuggestionsButton>
                    )}
                    {showMenu && (
                        <MenuWrapper>
                            <Menu
                                localization={localization}
                                disabled={hasError}
                                displaySnoozingControls={displaySnoozingControls}
                                isTrainerActive={isTrainerActive}
                                resumeTrainer={resumeTrainer}
                                snoozeTrainer={snoozeTrainer}
                                showDashboardLink={showDashboardLink}
                                setInfinitySnooze={setInfinitySnooze}
                                authToken={authToken}
                                doLogout={doLogout}
                                authWithPasswordRequired={authWithPasswordRequired}
                                enableCallSimulator={enableCallSimulator}
                            />
                        </MenuWrapper>
                    )}
                </div>
            </Wrapper>
        );
    }
}

export const PureHeader = Header;
export default Header;
